import { XMarkIcon } from '@heroicons/react/20/solid';
import {
  AdjustmentsHorizontalIcon,
  ArrowPathIcon,
  InboxIcon,
} from '@heroicons/react/24/outline';
import { Alert } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { DataTable } from '@/components/common/DataTable';
import { TableColumn } from '@/components/common/DataTable/types';
import { usePagination } from '@/hooks/usePagination';
import {
  tasksApiClient,
  useGetTaskQuery,
} from '@/services/api/tasksService';
import { Task } from '@/services/types/client-intake-types';
import { FilterRecord } from '@/services/types/pagination-types';
import { CanonicalTaskGroup } from '@/services/types/task-types';
import { selectUser } from '@/state/selectors/root-selectors';

import { AdvancedFiltersPanel } from './components/advanced-filters';
import { ColumnVisibilityMenu } from './components/ColumnVisibilityMenu';
import {
  GroupByOption,
  GroupBySelector,
} from './components/GroupBySelector';
import { RecordsRequestStatusFilter } from './components/RecordsRequestStatusFilter';
import { TaskDetailDrawer } from './components/TaskDetailDrawer';
import { TaskVerticalSwitcher } from './components/TaskVerticalSwitcher';
import {
  AdvancedFiltersMetadata,
  API_TO_COLUMN_FILTER_MAP,
  COLUMN_TO_API_FILTER_MAP,
} from './config/filterConfig';
import {
  getTaskGroupingConfigWithLabels,
  sortTasksForGrouping,
} from './config/groupByConfig';
import { getDefaultInitialFilters } from './config/metricsConfig';
import { VerticalConfig } from './config/taskVerticals';
import { useActiveFilterCount } from './hooks/useActiveFilterCount';
import { useColumnManager } from './hooks/useColumnManager';
import { useTaskBulkActions } from './hooks/useTaskBulkActions';
import { useTaskExport } from './hooks/useTaskExport';
import { useTaskListData } from './hooks/useTaskListData';

interface TaskListViewProps {
  verticalConfig: VerticalConfig;
  columns: TableColumn<Task>[];
  metricsComponent?: (props: {
    paginationState: ReturnType<typeof usePagination>['state'];
    paginationActions: ReturnType<typeof usePagination>['actions'];
    totalCount: number;
  }) => React.ReactNode;
  filterPillsComponent?: (props: {
    paginationState: ReturnType<typeof usePagination>['state'];
    paginationActions: ReturnType<typeof usePagination>['actions'];
  }) => React.ReactNode;
  advancedFiltersMetadata?: AdvancedFiltersMetadata;
}

/**
 * Task List View
 *
 * A reusable wrapper for the DataTable-based Task views that handles:
 * - Pagination state & URL synchronization
 * - Data fetching via useTaskListData
 * - Layout rendering (Title, Switcher, Metrics, Filters)
 * - DataTable with bulk actions & selection
 * - Task Detail Drawer management
 */
export const TaskListView = ({
  verticalConfig,
  columns,
  metricsComponent,
  filterPillsComponent,
  advancedFiltersMetadata,
}: TaskListViewProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = useSelector(selectUser);

  // Default to "Due Today" filter when user arrives without filters
  // Uses metricsConfig as single source of truth (see DEFAULT_INITIAL_METRIC_ID)
  const initialFilters = useMemo(
    () => getDefaultInitialFilters(currentUser?.id) as FilterRecord,
    [currentUser?.id],
  );

  const { state, actions, searchInput, setSearchInput } = usePagination({
    defaultLimit: 50,
    defaultSort: '-created_at',
    defaultFilters: verticalConfig.baseFilters,
    initialFilters,
    excludeParams: ['vertical', 'task', 'task_matter', 'cols', 'cols_v', 'groupBy', 'rrStatus'], // Exclude drawer params, column layout params, and client-side UI params so usePagination doesn't clear them
  });

  const { tasks, totalCount, isFetching, error } = useTaskListData(state);

  // Group By state - persisted in URL for shareability
  const groupByParam = (searchParams.get('groupBy') || 'none') as GroupByOption;

  // Records Request Status filter - persisted in URL for shareability
  // Client-side filter: filters the currently loaded page of tasks by TRR status
  const rrStatusParam = searchParams.get('rrStatus');
  const selectedRRStatuses = useMemo(
    () => (rrStatusParam ? rrStatusParam.split(',') : []),
    [rrStatusParam],
  );

  // Enable Group By when using the 'medical' vertical OR when Medical Records
  // canonical types are selected via the group pills (canonical_type_ids in URL).
  const groupByEnabled = useMemo(() => {
    if (verticalConfig.id === 'medical') return true;

    // Check if the selected canonical_type_ids all belong to Medical Records group
    const typeIds = state.filters.canonical_type_ids;
    const selectedIds = Array.isArray(typeIds)
      ? typeIds
      : typeof typeIds === 'string'
        ? [typeIds]
        : [];
    if (selectedIds.length === 0) return false;

    const types = advancedFiltersMetadata?.groupCounts?.types;
    if (!types) return false;

    // Get all type IDs that belong to the Medical Records group
    const medicalRecordTypeIds = new Set(
      types
        .filter((t) => t.group === CanonicalTaskGroup.MEDICAL_RECORDS)
        .map((t) => t.id),
    );

    return (
      selectedIds.length > 0 &&
      selectedIds.every((id) => medicalRecordTypeIds.has(id as string))
    );
  }, [
    verticalConfig.id,
    state.filters.canonical_type_ids,
    advancedFiltersMetadata?.groupCounts?.types,
  ]);

  const handleGroupByChange = useCallback(
    (value: GroupByOption) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value === 'none') {
          next.delete('groupBy');
        } else {
          next.set('groupBy', value);
        }
        return next;
      });
    },
    [setSearchParams],
  );

  const handleRRStatusChange = useCallback(
    (statuses: string[]) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (statuses.length === 0) {
          next.delete('rrStatus');
        } else {
          next.set('rrStatus', statuses.join(','));
        }
        return next;
      });
    },
    [setSearchParams],
  );

  // Apply client-side Records Request status filter, then sort/group
  const filteredByRRStatus = useMemo(() => {
    if (!groupByEnabled || selectedRRStatuses.length === 0) return tasks;
    return tasks.filter((task) => {
      const status = task.treatment_record_request?.status;
      return status ? selectedRRStatuses.includes(status) : false;
    });
  }, [tasks, selectedRRStatuses, groupByEnabled]);

  // Sort and group tasks when grouping is active
  const groupedTasks = useMemo(
    () =>
      groupByEnabled && groupByParam !== 'none'
        ? sortTasksForGrouping(filteredByRRStatus, groupByParam)
        : filteredByRRStatus,
    [filteredByRRStatus, groupByParam, groupByEnabled],
  );

  // Build grouping config for DataTable
  const groupingConfig = useMemo(
    () =>
      groupByEnabled
        ? getTaskGroupingConfigWithLabels(groupByParam)
        : undefined,
    [groupByParam, groupByEnabled],
  );

  // Memoize the group by selector to avoid re-creating on every render
  const groupBySelector = useMemo(
    () => (
      <GroupBySelector value={groupByParam} onChange={handleGroupByChange} />
    ),
    [groupByParam, handleGroupByChange],
  );

  // Memoize the records request status filter
  const rrStatusFilter = useMemo(
    () => (
      <RecordsRequestStatusFilter
        selectedStatuses={selectedRRStatuses}
        onChange={handleRRStatusChange}
      />
    ),
    [selectedRRStatuses, handleRRStatusChange],
  );

  // Column visibility and ordering with URL persistence
  const {
    displayColumns,
    visibleColumns,
    toggleColumn,
    setVisibleColumns,
    resetToDefault,
    hasChanges: hasColumnChanges,
    allColumns,
  } = useColumnManager({
    allColumns: columns,
    verticalId: verticalConfig.id,
  });

  // Memoize the column visibility menu to avoid re-creating on every render
  const columnVisibilityMenu = useMemo(
    () => (
      <ColumnVisibilityMenu
        allColumns={allColumns}
        visibleColumns={visibleColumns}
        onToggleColumn={toggleColumn}
        onReorderColumns={setVisibleColumns}
        onReset={resetToDefault}
        hasChanges={hasColumnChanges}
        label="Edit columns"
      />
    ),
    [
      allColumns,
      visibleColumns,
      toggleColumn,
      setVisibleColumns,
      resetToDefault,
      hasColumnChanges,
    ],
  );

  // Derived state from URL - Single Source of Truth
  // Both taskId and matterId are stored in URL so drawer doesn't depend on filtered task list
  const selectedTaskId = searchParams.get('task');
  const selectedMatterId = searchParams.get('task_matter');

  // Fetch detail data in background — the drawer opens immediately with list
  // data (or hover-prefetched data) and replaces with full detail when ready.
  const { data: selectedTaskData } = useGetTaskQuery(
    { taskId: selectedTaskId!, matterId: selectedMatterId! },
    {
      skip: !selectedTaskId || !selectedMatterId,
    },
  );

  const [isAdvancedFiltersOpen, setIsAdvancedFiltersOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

  const { copySuccess, handleShare, bulkActions } = useTaskBulkActions({
    tasks,
    selectedRows,
    setSelectedRows,
  });

  const { handleExport } = useTaskExport({
    queryState: state,
    visibleColumns,
    allColumns,
  });

  // Clear all: reset to blank slate (only base filters remain).
  // rrStatus/groupBy are passed as null so they are cleared in the same navigation call.
  const handleClearAll = useCallback(() => {
    setSearchInput('');
    actions.setAll({
      filters: { ...verticalConfig.baseFilters, rrStatus: null, groupBy: null },
      search: '',
      offset: 0,
      ordering: '',
    });
  }, [actions, setSearchInput, verticalConfig.baseFilters]);

  // Reset to default: restores initial filters (e.g., "My active tasks sorted by due date")
  const handleResetToDefault = useCallback(() => {
    setSearchInput('');
    actions.setAll({
      filters: {
        ...verticalConfig.baseFilters,
        ...initialFilters,
        rrStatus: null,
        groupBy: null,
      },
      search: '',
      offset: 0,
      ordering: initialFilters.ordering as string | undefined,
    });
  }, [actions, setSearchInput, verticalConfig.baseFilters, initialFilters]);

  // Prefetch task detail on hover so the drawer opens instantly on click
  const prefetchTask = tasksApiClient.usePrefetch('getTask');

  const handleRowHover = useCallback(
    (task: Task) => {
      prefetchTask({ taskId: task.id, matterId: task.matter });
    },
    [prefetchTask],
  );

  const handleRowClick = (task: Task) => {
    // Note: We intentionally do NOT clear selection here (FIN-924)
    // Users can deselect explicitly through the bulk actions toolbar
    // Update URL directly to open drawer, preserving other params
    // Store both taskId and matterId in URL so drawer doesn't depend on filtered task list
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('task', task.id);
      next.set('task_matter', task.matter);
      return next;
    });
  };

  const handleCloseTaskDetail = () => {
    // Remove task and task_matter params to close drawer
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('task');
      next.delete('task_matter');
      return next;
    });
  };

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
  };

  const handleAdvancedFiltersChange = useCallback(
    (newFilters: FilterRecord) => {
      // Start from current filters to preserve existing state (e.g. column filters)
      const mergedFilters: FilterRecord = { ...state.filters };

      // Apply new filters from the panel
      Object.entries(newFilters).forEach(([key, value]) => {
        if (
          value !== null &&
          value !== undefined &&
          value !== '' &&
          (!Array.isArray(value) || value.length > 0)
        ) {
          mergedFilters[key] = value;
        } else {
          // Remove empty/null/undefined values
          delete mergedFilters[key];
        }
      });

      // Enforce base filters (immutable per vertical)
      Object.assign(mergedFilters, verticalConfig.baseFilters);

      actions.setFilters(mergedFilters);
    },
    [actions, state.filters, verticalConfig.baseFilters],
  );

  const activeFiltersCount = useActiveFilterCount(
    state.filters,
    verticalConfig.baseFilters,
  );

  const handleFilterChange = (
    columnId: string,
    value: string | string[] | null,
  ) => {
    const filterKey = COLUMN_TO_API_FILTER_MAP[columnId] || columnId;
    const newFilters = { ...state.filters };

    if (value === null || (Array.isArray(value) && value.length === 0)) {
      delete newFilters[filterKey];
    } else {
      newFilters[filterKey] = value;
    }

    // Mutual exclusivity: when setting no_due_date, clear date range filters
    // (no_due_date means "no date at all", incompatible with date ranges)
    if (filterKey === 'no_due_date' && value === 'true') {
      delete newFilters.due_date_after;
      delete newFilters.due_date_before;
    }

    // Mutual exclusivity: when setting no_sla_resolution_target, clear SLA date range filters
    if (filterKey === 'no_sla_resolution_target' && value === 'true') {
      delete newFilters.sla_resolution_target_after;
      delete newFilters.sla_resolution_target_before;
    }

    actions.setFilters(newFilters);
  };

  const handleDateRangeFilterChange = (
    startKey: string,
    endKey: string,
    startDate?: string,
    endDate?: string,
  ) => {
    const newFilters = { ...state.filters };

    // Update or remove start date filter
    if (startDate) {
      newFilters[startKey] = startDate;
    } else {
      delete newFilters[startKey];
    }

    // Update or remove end date filter
    if (endDate) {
      newFilters[endKey] = endDate;
    } else {
      delete newFilters[endKey];
    }

    // Mutual exclusivity: clear "no date" flags when setting any date range
    // (these mean "no date at all", incompatible with date ranges)
    delete newFilters.no_due_date;
    delete newFilters.no_sla_resolution_target;

    actions.setFilters(newFilters);
  };

  const filterValues = useMemo(() => {
    const result: Record<string, string | string[] | null> = {};

    Object.entries(state.filters).forEach(([key, value]) => {
      const columnId = API_TO_COLUMN_FILTER_MAP[key] || key;
      if (typeof value === 'boolean') {
        result[columnId] = String(value);
      } else {
        result[columnId] = value as string | string[] | null;
      }
      // Also store the raw API key for daterange filters to look up
      // (daterange uses raw keys like 'due_date_after', not column IDs)
      if (key !== columnId) {
        result[key] = value as string | string[] | null;
      }
    });

    return result;
  }, [state.filters]);

  const emptyStateConfig = useMemo(
    () => ({
      icon: (
        <InboxIcon className="h-12 w-12 text-zinc-300 dark:text-zinc-600" />
      ),
      title: 'No tasks found',
      description: 'Try adjusting your search or filters.',
      action: {
        label: 'Reset to default',
        onClick: handleResetToDefault,
      },
    }),
    [handleResetToDefault],
  );

  return (
    <div
      className="flex flex-col gap-2 overflow-hidden bg-[#FAFAFA] px-10 py-2 dark:bg-zinc-950"
      style={{ height: 'calc(100vh - 64px)', contain: 'layout style' }}
    >
      {error && (
        <div className="p-6">
          <Alert severity="error">
            Failed to load tasks. Please try refreshing the page.
          </Alert>
        </div>
      )}

      {!error && (
        <>
          {/* Header Row */}
          <div className="flex flex-shrink-0 items-center justify-between">
            <div className="flex items-center gap-3">
              <h1 className="font-serif text-[32px] font-medium leading-[40px] text-black dark:text-white">
                Tasks
              </h1>
              <TaskVerticalSwitcher currentVertical={verticalConfig.id} />
            </div>

            {metricsComponent?.({
              paginationState: state,
              paginationActions: actions,
              totalCount,
            })}
          </div>

          {/* Filter Pills */}
          <div className="flex-shrink-0">
            {filterPillsComponent?.({
              paginationState: state,
              paginationActions: actions,
            })}
          </div>

          {/* Table */}
          <div
            className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-300 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900"
            style={{ contain: 'layout' }}
          >
            <DataTable
              data={groupedTasks}
              columns={displayColumns}
              getRowKey={(row) => row.id}
              searchEnabled
              searchPlaceholder="Search tasks by name or description..."
              searchValue={searchInput}
              onSearchChange={handleSearchChange}
              actions={[
                {
                  label: 'Reset to default filters',
                  icon: <ArrowPathIcon className="h-4 w-4 shrink-0" />,
                  onClick: handleResetToDefault,
                  variant: 'plain',
                  collapseAt: 'lg',
                },
                {
                  label: 'Clear all filters',
                  icon: <XMarkIcon className="h-4 w-4 shrink-0" />,
                  onClick: handleClearAll,
                  variant: 'plain',
                  collapseAt: 'lg',
                },
                {
                  label:
                    activeFiltersCount > 0
                      ? `Advanced filters (${activeFiltersCount})`
                      : 'Advanced filters',
                  icon: (
                    <AdjustmentsHorizontalIcon className="h-4 w-4 shrink-0" />
                  ),
                  onClick: () => setIsAdvancedFiltersOpen(true),
                  variant: 'plain',
                  className:
                    activeFiltersCount > 0
                      ? '!text-zinc-950 dark:!text-white'
                      : '',
                },
                ...(groupByEnabled
                  ? [
                      {
                        label: '',
                        customRender: () => rrStatusFilter,
                      },
                      {
                        label: '',
                        customRender: () => groupBySelector,
                      },
                    ]
                  : []),
                {
                  label: '',
                  customRender: () => columnVisibilityMenu,
                },
              ]}
              menuActions={[
                {
                  label: copySuccess ? 'Copied!' : 'Share view',
                  onClick: handleShare,
                },
                {
                  label: 'Export to CSV',
                  onClick: handleExport,
                },
              ]}
              selectionEnabled
              selectedRows={selectedRows}
              onSelectionChange={setSelectedRows}
              bulkActions={bulkActions}
              paginationEnabled
              currentPage={actions.getCurrentPage()}
              totalPages={actions.getTotalPages(totalCount)}
              totalCount={totalCount}
              pageSize={state.limit}
              onPageChange={actions.setPage}
              onPageSizeChange={actions.setLimit}
              onSortChange={actions.setOrdering}
              sortField={state.ordering ? state.ordering.split(',')[0].replace('-', '') : ''}
              sortDirection={state.ordering?.startsWith('-') ? 'desc' : 'asc'}
              onFilterChange={handleFilterChange}
              onDateRangeFilterChange={handleDateRangeFilterChange}
              filterValues={filterValues}
              isLoading={isFetching && tasks.length === 0}
              showProgressBar={isFetching}
              disableClientFilter
              emptyMessage="No tasks found"
              emptyStateConfig={emptyStateConfig}
              onRowClick={handleRowClick}
              onRowHover={handleRowHover}
              density="compact"
              getRowClassName={(row) =>
                row.id === selectedTaskId
                  ? '!bg-blue-50 !dark:bg-blue-900/30'
                  : ''
              }
              grouping={groupingConfig}
            />
          </div>
        </>
      )}

      {selectedTaskId && selectedMatterId && (
        <TaskDetailDrawer
          taskId={selectedTaskId}
          matterId={selectedMatterId}
          onClose={handleCloseTaskDetail}
          task={
            selectedTaskData ??
            tasks.find((t) => t.id === selectedTaskId)
          }
        />
      )}

      <AdvancedFiltersPanel
        isOpen={isAdvancedFiltersOpen}
        onClose={() => setIsAdvancedFiltersOpen(false)}
        filters={state.filters}
        onFiltersChange={handleAdvancedFiltersChange}
        groupCounts={advancedFiltersMetadata?.groupCounts}
        firmOptions={advancedFiltersMetadata?.firmOptions}
        assigneeOptions={advancedFiltersMetadata?.assigneeOptions}
        caseStatusOptions={advancedFiltersMetadata?.caseStatusOptions}
        priorityOptions={advancedFiltersMetadata?.priorityOptions}
        lastUpdateOptions={advancedFiltersMetadata?.lastUpdateOptions}
        statusOptions={advancedFiltersMetadata?.statusOptions}
        coverageOptions={advancedFiltersMetadata?.coverageOptions}
        baseFilters={verticalConfig.baseFilters}
      />
    </div>
  );
};
